/**
 * gapSuggestions — technology recommendations for closing ZT gaps.
 *
 * Client-side only. Maps capability keywords to suggested products,
 * approaches, and estimated effort. Used by sandbox gap analysis view.
 */

/**
 * Lookup table: capability keyword → technology suggestions.
 * Each suggestion includes product options, approach description,
 * and estimated implementation effort.
 */
const SUGGESTION_DB = {
  // IDENTITY
  'multi-factor authentication': {
    products: ['Okta Verify', 'Microsoft Authenticator', 'Duo Security', 'YubiKey'],
    approach: 'Deploy phishing-resistant MFA (FIDO2/WebAuthn) across all user populations. Start with privileged accounts, then extend to general workforce.',
    effort: '1-2 months',
    category: 'Identity & Access',
  },
  'privileged access management': {
    products: ['CyberArk', 'BeyondTrust', 'Delinea', 'HashiCorp Vault'],
    approach: 'Implement just-in-time privileged access with session recording, credential rotation, and break-glass procedures.',
    effort: '2-4 months',
    category: 'Identity & Access',
  },
  'identity governance': {
    products: ['SailPoint', 'Saviynt', 'Okta Identity Governance', 'Microsoft Entra ID Governance'],
    approach: 'Automate user lifecycle management with access certification campaigns, role mining, and separation-of-duties enforcement.',
    effort: '3-6 months',
    category: 'Identity & Access',
  },
  'single sign-on': {
    products: ['Okta', 'Microsoft Entra ID', 'Ping Identity', 'Auth0'],
    approach: 'Federate all applications through a central IdP using SAML/OIDC. Eliminate local application passwords.',
    effort: '1-3 months',
    category: 'Identity & Access',
  },
  'identity provider': {
    products: ['Okta', 'Microsoft Entra ID', 'Google Workspace', 'JumpCloud'],
    approach: 'Centralize identity management with a cloud-native IdP. Enforce consistent authentication policies across all resources.',
    effort: '1-2 months',
    category: 'Identity & Access',
  },

  // DEVICE
  'endpoint detection': {
    products: ['CrowdStrike Falcon', 'Microsoft Defender for Endpoint', 'SentinelOne', 'Carbon Black'],
    approach: 'Deploy EDR/XDR with behavioral analysis on all managed endpoints. Integrate with SIEM for correlated threat detection.',
    effort: '1-3 months',
    category: 'Endpoint Security',
  },
  'device compliance': {
    products: ['Microsoft Intune', 'Jamf Pro', 'VMware Workspace ONE', 'Kandji'],
    approach: 'Enforce device health checks (OS version, encryption, patching) before granting resource access. Block non-compliant devices.',
    effort: '2-3 months',
    category: 'Endpoint Security',
  },
  'patch management': {
    products: ['Automox', 'ManageEngine', 'Ivanti', 'WSUS/SCCM'],
    approach: 'Automate OS and third-party patching with risk-based prioritization. Target 72-hour SLA for critical patches.',
    effort: '1-2 months',
    category: 'Endpoint Security',
  },
  'mobile device management': {
    products: ['Microsoft Intune', 'Jamf Pro', 'VMware Workspace ONE', 'MobileIron'],
    approach: 'Enroll all corporate and BYOD devices in MDM. Enforce encryption, remote wipe, and containerization policies.',
    effort: '2-3 months',
    category: 'Endpoint Security',
  },

  // NETWORK
  'micro-segmentation': {
    products: ['Illumio', 'Guardicore (Akamai)', 'VMware NSX', 'Cisco ACI'],
    approach: 'Map application dependencies, then implement least-privilege network policies. Start with critical workloads.',
    effort: '3-6 months',
    category: 'Network Security',
  },
  'encrypted communications': {
    products: ['Cloudflare Tunnel', 'WireGuard', 'Mutual TLS (mTLS)', 'HTTPS Everywhere'],
    approach: 'Encrypt all internal east-west traffic using mTLS or VPN tunnels. Eliminate unencrypted protocols.',
    effort: '2-4 months',
    category: 'Network Security',
  },
  'zero trust network access': {
    products: ['Zscaler Private Access', 'Cloudflare Access', 'Palo Alto Prisma Access', 'Tailscale'],
    approach: 'Replace VPN with ZTNA. Authenticate and authorize every session based on identity, device, and context.',
    effort: '2-4 months',
    category: 'Network Security',
  },
  'network monitoring': {
    products: ['Darktrace', 'ExtraHop', 'Cisco Stealthwatch', 'Vectra AI'],
    approach: 'Deploy NDR for real-time traffic analysis and anomaly detection. Integrate with SIEM for correlated alerts.',
    effort: '1-3 months',
    category: 'Network Security',
  },

  // APPLICATION
  'application security testing': {
    products: ['Snyk', 'SonarQube', 'Checkmarx', 'Veracode'],
    approach: 'Integrate SAST/DAST/SCA into CI/CD pipelines. Block deployments with critical/high vulnerabilities. Track remediation SLAs.',
    effort: '1-3 months',
    category: 'Application Security',
  },
  'api security': {
    products: ['Salt Security', 'Noname Security', 'AWS API Gateway', 'Kong'],
    approach: 'Implement API gateway with authentication, rate limiting, and schema validation. Monitor for anomalous API behavior.',
    effort: '2-3 months',
    category: 'Application Security',
  },
  'application access control': {
    products: ['Okta Access Gateway', 'F5 BIG-IP APM', 'Akamai EAA', 'Cloudflare Access'],
    approach: 'Enforce identity-aware access policies per application. Implement step-up authentication for sensitive operations.',
    effort: '2-4 months',
    category: 'Application Security',
  },
  'container security': {
    products: ['Aqua Security', 'Sysdig', 'Prisma Cloud', 'Falco'],
    approach: 'Scan container images in CI/CD, enforce runtime security policies, and monitor for drift/anomalous behavior.',
    effort: '2-3 months',
    category: 'Application Security',
  },

  // DATA
  'data classification': {
    products: ['Microsoft Purview', 'BigID', 'Varonis', 'Spirion'],
    approach: 'Deploy automated data discovery and classification across all repositories. Label sensitive data for policy enforcement.',
    effort: '2-4 months',
    category: 'Data Protection',
  },
  'data loss prevention': {
    products: ['Microsoft Purview DLP', 'Symantec DLP', 'Digital Guardian', 'Forcepoint'],
    approach: 'Implement DLP controls at endpoint, network, and cloud layers. Monitor and block sensitive data exfiltration.',
    effort: '3-6 months',
    category: 'Data Protection',
  },
  'encryption at rest': {
    products: ['AWS KMS', 'Azure Key Vault', 'HashiCorp Vault', 'Google Cloud KMS'],
    approach: 'Ensure all data stores use encryption at rest with customer-managed keys. Implement key rotation policies.',
    effort: '1-2 months',
    category: 'Data Protection',
  },
  'backup and recovery': {
    products: ['Veeam', 'Cohesity', 'Rubrik', 'AWS Backup'],
    approach: 'Implement immutable backup strategy with tested recovery procedures. Target RPO/RTO aligned with business requirements.',
    effort: '2-3 months',
    category: 'Data Protection',
  },

  // VISIBILITY
  'siem': {
    products: ['Splunk', 'Microsoft Sentinel', 'Elastic Security', 'Sumo Logic'],
    approach: 'Centralize security event collection with automated alerting and correlation rules. Integrate all ZT pillar logs.',
    effort: '2-4 months',
    category: 'Visibility & Analytics',
  },
  'soar': {
    products: ['Palo Alto XSOAR', 'Splunk SOAR', 'Swimlane', 'Tines'],
    approach: 'Automate incident response playbooks for common alert types. Reduce mean-time-to-respond (MTTR) through orchestration.',
    effort: '2-4 months',
    category: 'Visibility & Analytics',
  },
  'threat intelligence': {
    products: ['Recorded Future', 'Mandiant', 'CrowdStrike Falcon Intelligence', 'MISP'],
    approach: 'Integrate threat intelligence feeds into SIEM/EDR detection rules. Correlate with internal telemetry for proactive defense.',
    effort: '1-2 months',
    category: 'Visibility & Analytics',
  },
  'incident response': {
    products: ['PagerDuty', 'Jira Service Management', 'ServiceNow SecOps', 'Opsgenie'],
    approach: 'Establish and regularly test IR plans. Define escalation paths, communication templates, and post-incident review processes.',
    effort: '1-2 months',
    category: 'Visibility & Analytics',
  },
};

/**
 * Find the best suggestion match for a capability name.
 * Uses fuzzy keyword matching against the suggestion database.
 */
export function getSuggestion(capabilityName) {
  if (!capabilityName) return null;
  const lower = capabilityName.toLowerCase();

  // Try exact match first
  if (SUGGESTION_DB[lower]) return SUGGESTION_DB[lower];

  // Try keyword containment
  for (const [key, value] of Object.entries(SUGGESTION_DB)) {
    const keywords = key.split(/\s+/);
    const matched = keywords.filter(k => lower.includes(k));
    if (matched.length >= Math.ceil(keywords.length * 0.6)) {
      return value;
    }
  }

  // Try reverse: capability name words in key
  const words = lower.split(/\s+/).filter(w => w.length > 3);
  for (const [key, value] of Object.entries(SUGGESTION_DB)) {
    const matched = words.filter(w => key.includes(w));
    if (matched.length >= 2) {
      return value;
    }
  }

  return null;
}

/**
 * Get all suggestions for a pillar's gaps.
 */
export function getSuggestionsForGaps(gaps) {
  return gaps.map(gap => ({
    ...gap,
    suggestion: getSuggestion(gap.capability),
  }));
}
